import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { CATEGORIES, MERCHANT_STATUS_INFO } from '@/lib/merchants';
import type { MerchantStatus } from '@/lib/merchants';
import { Search, X, Zap, SlidersHorizontal, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

const STATUS_ORDER: MerchantStatus[] = ['active', 'merchant', 'new'];

interface FilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  activeOnly: boolean;
  onActiveOnlyChange: (active: boolean) => void;
  verifiedOnly: boolean;
  onVerifiedOnlyChange: (verified: boolean) => void;
  totalCount: number;
  filteredCount: number;
}

export function FilterBar({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  activeOnly,
  onActiveOnlyChange,
  verifiedOnly,
  onVerifiedOnlyChange,
  totalCount,
  filteredCount,
}: FilterBarProps) {
  const [showFilters, setShowFilters] = useState(false);

  const categoryEntries = Object.entries(CATEGORIES);
  const hasFilters = searchQuery || selectedCategory || activeOnly || verifiedOnly;

  return (
    <div className="space-y-3">
      {/* Search + filter toggle row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search merchants..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 pr-9 h-10 bg-background"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="icon"
          className="size-10 shrink-0"
          onClick={() => setShowFilters(!showFilters)}
          aria-label="Toggle filters"
        >
          <SlidersHorizontal className="size-4" />
        </Button>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="space-y-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200">
          {/* Toggle filters */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck className="size-4 text-bch" />
                <span className="font-medium">Verified merchants only</span>
              </div>
              <Switch
                checked={verifiedOnly}
                onCheckedChange={onVerifiedOnlyChange}
              />
            </div>
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2 text-sm">
                <Zap className="size-4 text-bch" />
                <span className="font-medium">Active in last 24h only</span>
              </div>
              <Switch
                checked={activeOnly}
                onCheckedChange={onActiveOnlyChange}
              />
            </div>
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-1.5">
            <Badge
              variant={selectedCategory === null ? 'default' : 'outline'}
              className={cn(
                'cursor-pointer transition-all text-xs',
                selectedCategory === null && 'bg-bch text-bch-foreground hover:bg-bch/90',
              )}
              onClick={() => onCategoryChange(null)}
            >
              All
            </Badge>
            {categoryEntries.map(([key, { label, emoji }]) => (
              <Badge
                key={key}
                variant={selectedCategory === key ? 'default' : 'outline'}
                className={cn(
                  'cursor-pointer transition-all text-xs',
                  selectedCategory === key && 'bg-bch text-bch-foreground hover:bg-bch/90',
                )}
                onClick={() => onCategoryChange(selectedCategory === key ? null : key)}
              >
                {emoji} {label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Status legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
        {STATUS_ORDER.map(status => {
          const info = MERCHANT_STATUS_INFO[status];
          return (
            <div key={status} className="flex items-center gap-1.5" title={info.description}>
              <span className="relative flex shrink-0">
                {status === 'active' ? (
                  <>
                    <span
                      className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                      style={{ backgroundColor: info.dotColor }}
                    />
                    <span
                      className="relative inline-flex h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: info.dotColor }}
                    />
                  </>
                ) : (
                  <span
                    className="inline-flex h-2.5 w-2.5 rounded-full border border-border"
                    style={{ backgroundColor: info.dotColor }}
                  />
                )}
              </span>
              <span className="text-xs text-muted-foreground">{info.label}</span>
            </div>
          );
        })}
      </div>

      {/* Results count + clear */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>
          {filteredCount === totalCount
            ? `${totalCount} merchant${totalCount !== 1 ? 's' : ''}`
            : `${filteredCount} of ${totalCount} merchant${totalCount !== 1 ? 's' : ''}`}
        </span>
        {hasFilters && (
          <button
            onClick={() => {
              onSearchChange('');
              onCategoryChange(null);
              onActiveOnlyChange(false);
              onVerifiedOnlyChange(false);
            }}
            className="text-primary hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
